import type { TemplateResult } from "lit";
import { html, nothing } from "lit";
import type {
  FleetWorkspaceDensity,
  FleetWorkspaceLayoutState,
  FleetWorkspacePanelId,
  FleetWorkspacePreset,
} from "./fleet-workspace-presets.ts";

export type FleetWorkspacePanelBodies = Partial<Record<FleetWorkspacePanelId, unknown>>;

export type FleetWorkspacePanelMeta = {
  id: FleetWorkspacePanelId;
  title: string;
  description?: string;
  actions?: TemplateResult;
  /**
   * When true, the panel renders without a header chrome (no title,
   * no description, no action row). The body fills the full panel.
   * Used by the transcript panel in JAISIU-3080 to reclaim vertical
   * space for the conversation.
   */
  hideHeader?: boolean;
};

export type FleetWorkspaceLayoutProps = {
  preset: FleetWorkspacePreset;
  layout: FleetWorkspaceLayoutState;
  density: FleetWorkspaceDensity;
  panels: FleetWorkspacePanelBodies;
  panelMeta: FleetWorkspacePanelMeta[];
  onColumnResize: (index: number, ratio: number) => void;
  onRowResize: (index: number, ratio: number) => void;
  /**
   * JAISIU-3082 (operator tweak msg 2eff7eff): 0..2 index into
   * INSPECTOR_WIDTH_PRESETS. When provided, the operator layout
   * overrides --fleet-ws-col-b with the chosen pixel width on the
   * Inspector panel so the chat panel keeps its column.
   */
  inspectorWidth?: number;
  /**
   * JAISIU-3633: topbar Inspector toggle drives
   * `state.settings.fleetUnifiedDrawerOpen`. The unified preset reads
   * it via Lit's `?hidden` binding on the right column; the operator
   * preset renders the Inspector as a sibling panel that must be
   * conditionally shown. Pass `true` to render the panel, `false` to
   * omit it (so the toggle works for both presets).
   */
  sidePanelOpen?: boolean;
};

function panelVisible(layout: FleetWorkspaceLayoutState, id: FleetWorkspacePanelId): boolean {
  return layout.visiblePanels[id] !== false;
}

function renderPanelShell(
  meta: FleetWorkspacePanelMeta,
  body: unknown,
  density: FleetWorkspaceDensity,
) {
  const compact = density === "compact";
  const showHeader = !meta.hideHeader;
  return html`
    <section
      class="fleet-workspace__panel fleet-workspace__panel--${meta.id} ${meta.hideHeader ? "fleet-workspace__panel--headerless" : ""}"
      aria-label=${meta.title}
      data-panel=${meta.id}
    >
      ${
        showHeader
          ? html`
              <header
                class="fleet-workspace__panel-header ${compact ? "fleet-workspace__panel-header--compact" : ""}"
              >
                <div class="fleet-workspace__panel-heading">
                  <h3>${meta.title}</h3>
                  ${!compact && meta.description ? html`<p>${meta.description}</p>` : nothing}
                </div>
                ${meta.actions ?? nothing}
              </header>
            `
          : nothing
      }
      <div class="fleet-workspace__panel-body">${body ?? nothing}</div>
    </section>
  `;
}

function operatorColumnSplitRatios(layout: FleetWorkspaceLayoutState): number[] {
  const [, center, right] = layout.columns;
  const sum = center + right;
  if (sum <= 0) {
    return [0.5, 0.5];
  }
  return [center / sum, right / sum];
}

function rowSplitRatio(layout: FleetWorkspaceLayoutState): number {
  const [top, bottom] = layout.rows;
  const sum = top + bottom;
  return sum > 0 ? top / sum : 0.5;
}

function styleVars(
  layout: FleetWorkspaceLayoutState,
  preset: FleetWorkspacePreset,
): Record<string, string> {
  const cols = operatorColumnSplitRatios(layout);
  const row = rowSplitRatio(layout);
  return {
    "--fleet-ws-col-a": String(cols[0] ?? 0.5),
    "--fleet-ws-col-b": String(cols[1] ?? 0.5),
    "--fleet-ws-row-top": String(row),
    "--fleet-ws-row-bottom": String(1 - row),
  };
}

function renderRowHandle(
  index: number,
  ratio: number,
  onRowResize: (index: number, ratio: number) => void,
) {
  return html`
    <fleet-resize-handle
      orientation="row"
      .index=${index}
      .ratio=${ratio}
      @fleet-resize=${(e: CustomEvent<{ orientation: string; index: number; ratio: number }>) => {
        e.stopPropagation();
        onRowResize(e.detail.index, e.detail.ratio);
      }}
    ></fleet-resize-handle>
  `;
}

function metaFor(id: FleetWorkspacePanelId, panelMeta: FleetWorkspacePanelMeta[]) {
  return panelMeta.find((m) => m.id === id);
}

function renderOperatorLayout(props: FleetWorkspaceLayoutProps) {
  const { layout, panels, density, panelMeta } = props;
  const transcript = panels.transcript;
  const inspector = panels.inspector;
  const showTranscript = panelVisible(layout, "transcript");
  // JAISIU-3633: the operator preset previously had no mechanism to
  // hide the Inspector panel — `panelVisible()` only checks the static
  // `visiblePanels.inspector` flag, which is unrelated to the topbar
  // Inspector toggle (which flips `fleetUnifiedDrawerOpen`). When the
  // parent passes `sidePanelOpen === false`, omit the Inspector panel
  // entirely; otherwise fall back to the legacy `visiblePanels` check
  // so existing callers (chat-history sidebar, /files tab) keep the
  // pre-fix behaviour.
  const showInspector =
    panelVisible(layout, "inspector") && props.sidePanelOpen !== false;

  // JAISIU-3082 (operator tweak msg 2eff7eff): when the operator picks
  // a discrete Inspector width, expose it as --fleet-inspector-width
  // so the .fleet-workspace__panel--inspector CSS rule can override
  // its flex-basis and the chat panel keeps its current column.
  // No in-layout fleet-resize-handle — width is the Size picker, and the
  // solid 5px divider was a leftover right-edge border.
  // JAISIU-types-sweep: `Number.isInteger` is a `boolean`-returning
  // predicate, so it cannot narrow `number | undefined` on its own.
  // Reading the optional into a local and testing it explicitly keeps
  // the exact runtime behaviour (missing / non-integer index → the
  // default `1`) while letting the `| 0` bit-truncation type-check.
  const rawInspectorWidth = props.inspectorWidth;
  const inspectorWidthIdx =
    rawInspectorWidth !== undefined && Number.isInteger(rawInspectorWidth)
      ? Math.max(0, Math.min(2, rawInspectorWidth | 0))
      : 1;
  const inspectorWidthPx = [280, 460, 600][inspectorWidthIdx];

  return html`
    <div
      class="fleet-workspace fleet-workspace--operator"
      style=${`${Object.entries(styleVars(layout, "operator"))
        .map(([k, v]) => `${k}:${v}`)
        .join(";")};--fleet-inspector-width:${inspectorWidthPx}px`}
    >
      ${
        showTranscript
          ? renderPanelShell(
              metaFor("transcript", panelMeta) ?? {
                id: "transcript",
                title: "Chat transcript",
              },
              transcript,
              density,
            )
          : nothing
      }
      ${
        showInspector
          ? renderPanelShell(
              metaFor("inspector", panelMeta) ?? {
                id: "inspector",
                title: "Inspector",
              },
              inspector,
              density,
            )
          : nothing
      }
    </div>
  `;
}

export function renderFleetWorkspaceLayout(props: FleetWorkspaceLayoutProps) {
  // Only `unified` (default) and `operator` presets remain. `unified`
  // is rendered by `renderFleetUnifiedLayout` upstream; here everything
  // funnels through the operator renderer, which the unified layout
  // shell also falls back to.
  return renderOperatorLayout(props);
}

/** Apply a column drag ratio back into layout.columns for the operator preset. */
export function applyFleetColumnResize(
  preset: FleetWorkspacePreset,
  layout: FleetWorkspaceLayoutState,
  index: number,
  ratio: number,
): FleetWorkspaceLayoutState {
  const r = Math.max(0.15, Math.min(0.85, ratio));
  const next = {
    ...layout,
    columns: [...layout.columns] as [number, number, number],
    rows: [...layout.rows] as [number, number],
    visiblePanels: { ...layout.visiblePanels },
  };
  if (preset === "operator") {
    next.columns = [0, r, 1 - r];
  } else if (index === 0) {
    next.columns = [r, 1 - r, 0];
  }
  return next;
}

/** Apply a row drag ratio back into layout.rows. */
export function applyFleetRowResize(
  layout: FleetWorkspaceLayoutState,
  ratio: number,
): FleetWorkspaceLayoutState {
  const r = Math.max(0.2, Math.min(0.8, ratio));
  return {
    ...layout,
    columns: [...layout.columns] as [number, number, number],
    rows: [r, 1 - r],
    visiblePanels: { ...layout.visiblePanels },
  };
}

// `renderRowHandle` is exported indirectly via the operator renderer,
// but keep an explicit re-export so future layouts can mount a 2-row
// shell without re-importing from a private function.
export { renderRowHandle };
